import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import { assetUrl } from '../utils/asset';

type RainPoint = { lat: number; lon: number; v: number };
type RainDaily = { date: string; grid_step: number; points: RainPoint[] };
type Rain7 = { start_date: string; end_date: string; grid_step: number; points: RainPoint[] };

type Mode =
  | { kind: 'daily'; date: string }
  | { kind: 'week7'; start: string; end: string };

export default function RainOverlay({ mode, visible = true }: { mode: Mode; visible?: boolean }) {
  const map = useMap();
  const layerRef = useRef<L.LayerGroup | null>(null);
  const [data, setData] = useState<RainDaily | Rain7 | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const url =
          mode.kind === 'daily'
            ? assetUrl(`/era5_sm/rain_${mode.date}.json`)
            : assetUrl(`/era5_sm/rain7_${mode.start}_${mode.end}.json`);
        console.debug('[RainOverlay] fetching', url);
        const res = await fetch(url);
        if (!res.ok) {
          console.debug('[RainOverlay] fetch non-ok status', url, res.status);
          return;
        }
        const json = await res.json();
        if (!cancelled) {
          console.debug('[RainOverlay] data loaded ok', url, 'points=', Array.isArray((json as any)?.points) ? (json as any).points.length : 'n/a');
          setData(json);
        }
      } catch {}
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  useEffect(() => {
    if (!map) return;
    if (!layerRef.current) {
      // Ensure the contours pane exists with lower z-index than the border
      const contourPaneName = 'contour-pane';
      if (!map.getPane(contourPaneName)) {
        map.createPane(contourPaneName);
      }
      const contourPane = map.getPane(contourPaneName);
      if (contourPane) {
        contourPane.style.zIndex = '450';
        contourPane.style.pointerEvents = 'none';
      }
      layerRef.current = L.layerGroup();
      layerRef.current.addTo(map);
    }
    return () => {
      if (layerRef.current) {
        layerRef.current.remove();
        layerRef.current = null;
      }
    };
  }, [map]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!visible) return;
    if (!data) return;
    console.debug('[RainOverlay] rendering rectangles. visible=', visible, 'mode=', mode.kind);
    const gridStep = (data as any).grid_step || 0.25;
    const half = gridStep / 2;
    const rectangles: L.Rectangle[] = [];
    // downsample to ~0.5° for performance
    const sampleEvery = Math.max(1, Math.round(0.5 / gridStep));
    const maxVal = (mode.kind === 'daily') ? 300 : 1000; // clamp for visualization
    const colorFor = (v: number) => {
      const vv = Math.max(0, Math.min(maxVal, v));
      const t = vv / maxVal;
      // interpolate from light blue to dark blue
      const c0 = [210, 230, 255];
      const c1 = [0, 62, 126];
      const r = Math.round(c0[0] + (c1[0] - c0[0]) * t);
      const g = Math.round(c0[1] + (c1[1] - c0[1]) * t);
      const b = Math.round(c0[2] + (c1[2] - c0[2]) * t);
      return `rgba(${r},${g},${b},0.75)`;
    };

    // Build a fast index-based downsample by grid
    const invStep = 1 / gridStep;
    for (const p of (data as any).points as RainPoint[]) {
      const iLat = Math.round(p.lat * invStep);
      const iLon = Math.round(p.lon * invStep);
      if (iLat % sampleEvery !== 0 || iLon % sampleEvery !== 0) continue;
      if (!Number.isFinite(p.v)) continue;
      const bounds: L.LatLngBoundsExpression = [
        [p.lat - half, p.lon - half],
        [p.lat + half, p.lon + half],
      ];
      const rect = L.rectangle(bounds, {
        stroke: false,
        fill: true,
        fillOpacity: 0.85,
        color: colorFor(p.v),
        pane: 'contour-pane',
      });
      rect.addTo(layer);
      rectangles.push(rect);
    }

    // Add a small legend control (static for now)
  const legend = new L.Control({ position: 'bottomright' });
  (legend as any).onAdd = () => {
      const div = L.DomUtil.create('div', 'rain-legend');
      div.style.background = 'rgba(255,255,255,0.8)';
      div.style.padding = '6px 8px';
      div.style.borderRadius = '6px';
      div.style.fontSize = '12px';
      const title = mode.kind === 'daily' ? `Rain (mm) ${ (data as any).date }` : `7-day Rain (mm) ${ (data as any).start_date }..${ (data as any).end_date }`;
      div.innerHTML = `
        <div style="font-weight:700; margin-bottom:4px">${title}</div>
        <div style="display:flex; align-items:center; gap:6px">
          <div style="width:120px; height:10px; background: linear-gradient(to right, rgb(210,230,255), rgb(0,62,126)); border:1px solid rgba(0,0,0,0.15)"></div>
          <div style="display:flex; justify-content:space-between; width:130px">
            <span>0</span><span>${maxVal/2}</span><span>${maxVal}</span>
          </div>
        </div>`;
      return div;
    };
    legend.addTo(map);

    return () => {
      layer.clearLayers();
      legend.remove();
    };
  }, [data, visible, map, mode]);

  return null;
}
